import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query'
import { authKeys } from '@/features/auth/api'
import { api, unwrap } from '@/lib/api'
import type { DataEnvelope, Location, Paginated } from '@/types/api'

export type LocationSort = 'name' | '-created_at'

export interface LocationListParams {
  search?: string
  sort?: LocationSort
}

export type LocationInput = Omit<Location, 'id' | 'displays_count' | 'created_at' | 'updated_at' | 'default_channel_id' | 'fallback_channel_id'> &
  Partial<Pick<Location, 'default_channel_id' | 'fallback_channel_id'>>

export const locationKeys = {
  all: ['locations'] as const,
  lists: () => [...locationKeys.all, 'list'] as const,
  list: (params: LocationListParams) => [...locationKeys.lists(), params] as const,
  details: () => [...locationKeys.all, 'detail'] as const,
  detail: (id: string) => [...locationKeys.details(), id] as const,
}

export function locationsInfiniteOptions(params: LocationListParams) {
  return infiniteQueryOptions({
    queryKey: locationKeys.list(params),
    queryFn: ({ pageParam, signal }) =>
      api.get<Paginated<Location>>('/locations', { signal, query: { search: params.search, sort: params.sort, cursor: pageParam, per_page: 25 } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.meta?.next_cursor ?? undefined,
  })
}

export function useLocations(params: LocationListParams) {
  return useInfiniteQuery(locationsInfiniteOptions(params))
}

export function locationOptions(id: string) {
  return queryOptions({
    queryKey: locationKeys.detail(id),
    queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<Location>>(`/locations/${id}`, { signal })),
  })
}

export function useLocation(id: string) {
  return useQuery(locationOptions(id))
}

function invalidateUsage(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: authKeys.me() })
  void qc.invalidateQueries({ queryKey: ['dashboard'] })
}

export function useCreateLocation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: LocationInput) => unwrap(api.post<DataEnvelope<Location>>('/locations', input)),
    onSuccess: (location) => {
      qc.setQueryData(locationKeys.detail(location.id), location)
      void qc.invalidateQueries({ queryKey: locationKeys.lists() })
      void qc.invalidateQueries({ queryKey: ['onboarding'] })
      invalidateUsage(qc)
    },
  })
}

export function useUpdateLocation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: Partial<LocationInput>) => unwrap(api.patch<DataEnvelope<Location>>(`/locations/${id}`, input)),
    onSuccess: (location) => {
      qc.setQueryData(locationKeys.detail(id), location)
      // Patch the row in any cached list so the table updates without a refetch flash.
      qc.setQueriesData<InfiniteData<Paginated<Location>>>({ queryKey: locationKeys.lists() }, (data) =>
        data ? { ...data, pages: data.pages.map((p) => ({ ...p, data: p.data.map((l) => (l.id === id ? location : l)) })) } : data,
      )
      void qc.invalidateQueries({ queryKey: locationKeys.lists() })
    },
  })
}

export function useDeleteLocation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/locations/${id}`),
    // Optimistic removal from lists; rolled back on failure (e.g. 409 displays attached).
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: locationKeys.lists() })
      const snapshot = qc.getQueriesData<InfiniteData<Paginated<Location>>>({ queryKey: locationKeys.lists() })
      qc.setQueriesData<InfiniteData<Paginated<Location>>>({ queryKey: locationKeys.lists() }, (data) =>
        data ? { ...data, pages: data.pages.map((p) => ({ ...p, data: p.data.filter((l) => l.id !== id) })) } : data,
      )
      return { snapshot }
    },
    onError: (_e, _id, ctx) => {
      ctx?.snapshot.forEach(([key, data]) => qc.setQueryData(key, data))
    },
    onSuccess: (_d, id) => {
      qc.removeQueries({ queryKey: locationKeys.detail(id) })
      invalidateUsage(qc)
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: locationKeys.lists() }),
  })
}

/** Up to 100 locations by name — for pickers (display forms, filters). */
export function useLocationOptions(enabled = true) {
  return useQuery({
    queryKey: [...locationKeys.lists(), 'options'] as const,
    queryFn: ({ signal }) => api.get<Paginated<Location>>('/locations', { signal, query: { sort: 'name', per_page: 100 } }),
    select: (page) => page.data,
    enabled,
    staleTime: 60_000,
  })
}
